import React, { createContext, useState, useEffect } from 'react';
import servicesData from '../datos/servicios.json';
import seimaDB from '../datos/seima_products_db.json';
import brandsData from '../datos/marcas.json';
import testimonialsData from '../datos/testimonios.json';

export const DataContext = createContext();

const SECTIONS = [
  { id: 'sec-nosotros',  name: 'Nosotros',              keywords: 'quienes somos empresa nosotros about',           path: '/nosotros',    anchor: null,       category: 'Página' },
  { id: 'sec-historia',  name: 'Historia',               keywords: 'historia trayectoria fundacion fundación años',  path: '/',            anchor: 'historia', category: 'Sección' },
  { id: 'sec-servicios', name: 'Servicios',              keywords: 'servicios servicio que hacemos soluciones',      path: '/servicios',   anchor: null,       category: 'Página' },
  { id: 'sec-productos', name: 'Productos',              keywords: 'productos producto catalogo catálogo equipos',    path: '/productos',   anchor: null,       category: 'Página' },
  { id: 'sec-marcas',    name: 'Marcas',                 keywords: 'marcas brands ipso unimac milnor distribuimos',  path: '/',            anchor: 'marcas',   category: 'Sección' },
  { id: 'sec-clientes',  name: 'Clientes',               keywords: 'clientes empresas atendidas partners',           path: '/',            anchor: 'clientes', category: 'Sección' },
  { id: 'sec-ubicacion', name: 'Ubicación',              keywords: 'ubicacion ubicación donde mapa dirección oficina', path: '/',          anchor: 'location', category: 'Sección' },
  { id: 'sec-contacto',  name: 'Contacto',               keywords: 'contacto telefono teléfono correo email escribir', path: '/',          anchor: 'contact',  category: 'Sección' },
  { id: 'sec-faq',       name: 'Preguntas Frecuentes',   keywords: 'faq preguntas frecuentes dudas respuestas',      path: '/',            anchor: 'faq',      category: 'Sección' },
  { id: 'sec-limpieza',   name: 'Limpieza de Tuberías',   keywords: 'limpieza tuberia tubería industrial sarro alta presion',    path: '/servicios/limpieza-tuberia-industrial',       anchor: null, category: 'Servicio' },
  { id: 'sec-mant',      name: 'Mantenimiento de Equipo',keywords: 'mantenimiento preventivo correctivo lavandería lavanderia', path: '/servicios/mantenimiento-lavanderia-industrial', anchor: null, category: 'Servicio' },
  { id: 'sec-reparacion',name: 'Reparación de Equipo',   keywords: 'reparacion reparación falla equipo lavanderia emergencia', path: '/servicios/reparacion-lavanderia-industrial',   anchor: null, category: 'Servicio' },
  { id: 'sec-domus',     name: 'Repuestos DOMUS',         keywords: 'repuestos domus refacciones piezas partes lavanderia',   path: '/servicios/repuestos-domus-lavanderia',         anchor: null, category: 'Servicio' },
];

const enrichProduct = (product) => {
  const name = (product.name || '').toLowerCase();
  const category = (product.category || '').toLowerCase();
  const use = (product.use || '').toLowerCase();
  const brand = product.brand || 'Otras';

  let sector = 'Otros';
  if (category.includes('barrera sanitaria') || use.includes('sanitario') || use.includes('hospital') || use.includes('farmacéutica') || name.includes('farmacéutica')) {
    sector = 'Salud y Farmacéutica';
  } else if (category.includes('polvo') || use.includes('carpintería') || category.includes('ciclones')) {
    sector = 'Carpintería y Control de Polvo';
  } else if (use.includes('tuberías') || use.includes('chillers') || use.includes('calderas')) {
    sector = 'Mantenimiento Térmico / HVAC';
  } else if (use.includes('industrial pesada') || category.includes('industriales') || category.includes('hidrolavadoras')) {
    sector = 'Industria Pesada y Manufactura';
  } else if (use.includes('comercial') || use.includes('hoteles') || use.includes('retail') || use.includes('oficinas')) {
    sector = 'Hotelería, Comercial y Edificios';
  } else if (category.includes('lavandería')) {
    sector = 'Lavandería Comercial e Industrial';
  }

  let specialties = [];
  if (name.includes('atex') || use.includes('atex')) specialties.push('Certificación ATEX');
  if (category.includes('barrera sanitaria')) specialties.push('Grado Sanitario');
  if (name.includes('ecotank') || category.includes('recuperación de agua') || name.includes('eco-energy')) specialties.push('Sistemas Eco / Recuperación');
  if (specialties.length === 0) specialties.push('Equipo Estándar');

  const availability = product.on_demand ? 'Bajo Pedido' : 'En Stock';

  return {
    ...product,
    filters: {
      brand: brand === 'Por confirmar' ? 'Otras' : brand,
      sector,
      specialties,
      availability
    }
  };
};

export const DataProvider = ({ children }) => {
  const [services] = useState(servicesData);
  const [products] = useState(() => seimaDB.products.map(enrichProduct));
  const [categories] = useState(seimaDB.categories);
  const [brands] = useState(brandsData);
  const [testimonials] = useState(testimonialsData);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [quickViewProduct, setQuickViewProduct] = useState(null);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const query = searchQuery.toLowerCase();

    const matchedCategories = categories.filter(c =>
      c.name.toLowerCase().includes(query) ||
      (c.slug && c.slug.toLowerCase().includes(query))
    ).map(c => ({
      id: `cat-${c.slug}`,
      name: `Ver todo en ${c.name}`,
      category: 'Categoría de Productos',
      type: 'product_category',
      slug: c.slug
    }));

    const matchedServices = services.filter(s =>
      s.name.toLowerCase().includes(query) ||
      s.shortDescription.toLowerCase().includes(query) ||
      s.description.toLowerCase().includes(query) ||
      (s.specs || []).some(sp => sp.toLowerCase().includes(query))
    ).map(s => ({ ...s, type: 'service', category: 'Servicio' }));

    const matchedProducts = products.filter(p =>
      p.name.toLowerCase().includes(query) ||
      (p.model && p.model.toLowerCase().includes(query)) ||
      (p.brand && p.brand.toLowerCase().includes(query)) ||
      (p.category && p.category.toLowerCase().includes(query)) ||
      (p.type && p.type.toLowerCase().includes(query)) ||
      (p.use && p.use.toLowerCase().includes(query)) ||
      (p.description && p.description.toLowerCase().includes(query))
    ).map(p => ({ ...p, type: 'product', category: 'Producto' }));

    const matchedSections = SECTIONS.filter(s =>
      s.name.toLowerCase().includes(query) ||
      s.keywords.toLowerCase().includes(query)
    ).map(s => ({ ...s, type: 'section' }));

    setSearchResults([...matchedCategories, ...matchedServices, ...matchedProducts, ...matchedSections]);
  }, [searchQuery, services, products, categories]);

  return (
    <DataContext.Provider value={{
      services,
      products,
      categories,
      brands,
      testimonials,
      searchQuery,
      setSearchQuery,
      searchResults,
      quickViewProduct,
      setQuickViewProduct
    }}>
      {children}
    </DataContext.Provider>
  );
};
export default DataProvider;
